import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Reveal } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

export default function JournalPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/journal/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setMissing(true));
  }, [slug]);

  return (
    <section data-testid="journal-article" className="bg-ivory pb-28 pt-36 md:pt-48">
      <div className="mx-auto max-w-3xl px-6 md:px-0">
        <Reveal>
          <Link
            to="/journal"
            data-testid="journal-back-link"
            className="group inline-flex items-center gap-2 font-label text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-500 transition-colors duration-300 hover:text-terra"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
            Back to Journal
          </Link>
        </Reveal>

        {post && (
          <article className="mt-12">
            <Reveal delay={0.05}>
              <p className="font-label text-[10px] font-semibold uppercase tracking-[0.26em] text-terra">
                {post.category} <span className="text-stone-400">· {formatDate(post.date)} · {post.read_time}</span>
              </p>
            </Reveal>
            <Reveal delay={0.12}>
              <h1 className="mt-6 font-serif text-4xl leading-[1.12] tracking-tight text-ink md:text-6xl">{post.title}</h1>
            </Reveal>
            <Reveal delay={0.2}>
              <span className="mt-10 block h-px w-24 bg-terra" />
            </Reveal>
            {post.image && (
              <Reveal delay={0.25}>
                <div className="mt-12 overflow-hidden" data-testid="journal-cover">
                  <img src={post.image} alt={post.title} className="aspect-[16/9] w-full object-cover" />
                </div>
              </Reveal>
            )}
            <div className="mt-10 space-y-7">
              {post.body.map((para, i) => (
                <Reveal key={i} delay={0.05 * i} y={20}>
                  <p className="text-base leading-relaxed text-stone-700 md:text-lg">{para}</p>
                </Reveal>
              ))}
            </div>
          </article>
        )}

        {missing && (
          <p className="mt-16 font-serif text-3xl italic text-stone-400" data-testid="journal-not-found">
            This note couldn't be found.
          </p>
        )}

        <Reveal delay={0.2}>
          <div className="noise-overlay mt-20 bg-ink p-10 text-ivory md:p-14" data-testid="journal-cta">
            <div className="relative z-10">
              <h2 className="font-serif text-2xl leading-snug md:text-3xl">
                Working through this yourself? <span className="italic text-terra-light">Let's talk product.</span>
              </h2>
              <Link
                to="/contact"
                data-testid="journal-cta-button"
                className="group mt-8 inline-flex items-center gap-3 bg-terra px-8 py-4 font-label text-xs font-semibold uppercase tracking-[0.2em] text-ivory transition-colors duration-300 hover:bg-terra-dark"
              >
                Start a Project
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
